import axios from './index';
import {
    postUserShareAttemptsActivitiesByActivityIdFinalize,
    postUserShareAttemptsByAttemptIdApprove,
    postUserShareAttemptsByAttemptIdMetrics,
} from './generated/user-share';

import type { ApiResponse } from './dramas';

export type UserShareAttemptStatus = 'pending_approval' | 'approved' | 'rejected' | 'unsubmitted';

/** 列表 metrics：库表字段 play_count / like_count / re_tweet_count，缺省或 null 按 0 */
export interface UserShareAttemptMetrics {
    play_count?: number | string | null;
    like_count?: number | string | null;
    re_tweet_count?: number | string | null;
    playCount?: number | string | null;
    likeCount?: number | string | null;
    reTweetCount?: number | string | null;
    /** 兼容旧命名 */
    play?: number | string | null;
    like?: number | string | null;
    retweet?: number | string | null;
    plays?: number | string | null;
    likes?: number | string | null;
    shares?: number | string | null;
    [property: string]: unknown;
}

export interface UserShareAttempt {
    id: string;
    attempt_id?: string;
    activity_id?: string;
    user_id?: string;
    uid?: string;
    username?: string;
    user_name?: string;
    platform?: string;
    link?: string;
    url?: string;
    share_url?: string;
    captcha?: string;
    verify_code?: string;
    code?: string;
    status?: UserShareAttemptStatus | string;
    reject_reason?: string;
    rejection_reason?: string;
    reason?: string;
    submitted_at?: string;
    created_at?: string;
    updated_at?: string;
    auto_approve_at?: string;
    auto_approve_deadline?: string;
    auto_pass_at?: string;
    approved_at?: string;
    rejected_at?: string;
    /** 传播指标；无统计记录时为 null */
    metrics?: UserShareAttemptMetrics | null;
    /** 旧平铺字段，兼容保留 */
    plays?: number | string;
    likes?: number | string;
    shares?: number | string;
    play?: number | string;
    like?: number | string;
    retweet?: number | string;
    [property: string]: unknown;
}

/** 列表筛选 status：与后端校验枚举对齐 */
export type UserShareAttemptStatusQuery = UserShareAttemptStatus;

export interface UserShareAttemptListParams {
    page?: number | string;
    size?: number | string;
    /** 全部不传 */
    status?: UserShareAttemptStatusQuery;
}

export interface UserShareAttemptMetricsPayload {
    plays?: number;
    likes?: number;
    shares?: number;
}

/** null / 缺省 → 0（后端：metrics 或各 count 可能为 null） */
const toMetricNumber = (value?: number | string | null) => {
    if (value === undefined || value === null || value === '') return 0;
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

/** 列表展示用指标，恒返回 number；metrics 为 null 时三项均为 0 */
export const resolveUserShareMetrics = (
    row?: Pick<UserShareAttempt, 'metrics' | 'plays' | 'likes' | 'shares' | 'play' | 'like' | 'retweet'> | null,
): { plays: number; likes: number; shares: number } => {
    if (row?.metrics === null) {
        return { plays: 0, likes: 0, shares: 0 };
    }
    if (row?.metrics && typeof row.metrics === 'object') {
        const m = row.metrics as UserShareAttemptMetrics;
        return {
            plays: toMetricNumber(m.play_count ?? m.playCount ?? m.play ?? m.plays),
            likes: toMetricNumber(m.like_count ?? m.likeCount ?? m.like ?? m.likes),
            shares: toMetricNumber(m.re_tweet_count ?? m.reTweetCount ?? m.retweet ?? m.shares),
        };
    }
    return {
        plays: toMetricNumber(row?.play ?? row?.plays),
        likes: toMetricNumber(row?.like ?? row?.likes),
        shares: toMetricNumber(row?.retweet ?? row?.shares),
    };
};

export interface UserShareAttemptFinalizePayload {
    finalizedBy?: string;
}

export const userShareAttemptList = (params?: UserShareAttemptListParams) =>
    axios<ApiResponse<UserShareAttempt[]>>('/v1/user-share-attempts', {
        method: 'get',
        params,
    });

export const userShareAttemptApprove = (attemptId: string) => postUserShareAttemptsByAttemptIdApprove(attemptId);

/** OpenAPI 未声明 body；原型需要拒绝理由 */
export const userShareAttemptReject = (attemptId: string, data: { reason: string }) =>
    axios<ApiResponse>(`/v1/user-share-attempts/${attemptId}/reject`, {
        method: 'post',
        data,
    });

export const userShareAttemptUpdateMetrics = (attemptId: string, data: UserShareAttemptMetricsPayload) =>
    postUserShareAttemptsByAttemptIdMetrics(attemptId, data as never);

export const userShareActivityFinalize = (activityId: string, data?: UserShareAttemptFinalizePayload) =>
    postUserShareAttemptsActivitiesByActivityIdFinalize(activityId, data as never);
